/**
 * BHA write layer + full-job roll-up — injected into the shared Oxbo-P client
 * without touching src/api/pApi.ts (so branches merge cleanly). BHA is
 * event-scoped and keyed by Run (§10.3). All request/response types the write
 * layer needs are declared locally here.
 *
 * The read of the runs list already exists in pApi as `useGetEventBhaRunsQuery`,
 * but that query carries no cache tag, so a mutation can't invalidate it. We
 * re-declare the list read here under a `BhaRuns` tag and use THIS hook in the
 * edit panel, so a successful POST/PUT refetches the list automatically.
 */
import { pApi } from "@api/pApi";
import type { BhaRunOut, BhaRunsResponse } from "@api/types";

/**
 * The shared BhaRunOut in @api/types predates the write layer and omits the
 * write-key fields. The live contract returns `entity_id` + `version` on every
 * run (PUT keys on entity_id, not id), so we extend it locally here rather than
 * touch the shared types file.
 */
export interface BhaRunWithEntity extends BhaRunOut {
  entity_id: string;
  version?: number;
}

/** The runs list response, typed with the entity-bearing run. */
export interface BhaRunsListResponse extends Omit<BhaRunsResponse, "runs"> {
  runs: BhaRunWithEntity[];
}

// ── Local write-layer request types (mirror Oxbo-P BitOpCreate / BhaRun{Create,Update}) ──

/** One bit-operation line added to a run (PUT appends these to a run's bit_ops). */
export interface BitOpCreate {
  daily_report_id?: string | null;
  date_op?: string | null;
  md_op?: number | null;
  op_duration?: number | null;
  op_progress?: number | null;
  rop?: number | null;
  wob_min?: number | null;
  wob_max?: number | null;
  rpm_min?: number | null;
  rpm_max?: number | null;
  flowrate?: number | null;
  pressure_standpipe?: number | null;
  activity_code_entry_key?: string | null;
}

/** POST /events/{event_id}/bha-runs — well_uwi required. */
export interface BhaRunCreate {
  well_uwi: string;
  wellbore_uwi?: string | null;
  assembly_id?: string | null;
  date_in?: string | null;
  date_out?: string | null;
  md_in?: number | null;
  md_out?: number | null;
  bit_ops?: BitOpCreate[];
  change_reason?: string | null;
}

/**
 * PUT /events/{event_id}/bha-runs/{entity_id} — every field optional; any
 * bit_ops supplied are APPENDED to the run (not replaced).
 */
export interface BhaRunUpdate {
  well_uwi?: string;
  wellbore_uwi?: string | null;
  assembly_id?: string | null;
  date_in?: string | null;
  date_out?: string | null;
  md_in?: number | null;
  md_out?: number | null;
  bit_ops?: BitOpCreate[];
  change_reason?: string | null;
}

// ── Local full-job roll-up response types (GET /wells/{uwi}/full-job/bha) ──

/** Summary by section: one row per hole section across the whole well. */
export interface BhaSectionRow {
  section: string | null;
  run_count: number;
  footage: number | null;
  md_in_min: number | null;
  md_out_max: number | null;
}

/** Flattened by-Run row across every event on the well. */
export interface BhaFullJobRunRow {
  id: string;
  entity_id: string;
  event_id: string;
  well_uwi: string;
  section: string | null;
  date_in: string | null;
  date_out: string | null;
  md_in: number | null;
  md_out: number | null;
  footage: number | null;
  bit_op_count: number;
}

export interface BhaFullJobResponse {
  well_uwi: string;
  unit_profile_key: string | null;
  by_section: BhaSectionRow[];
  by_run: BhaFullJobRunRow[];
}

// The list read returns the shared BhaRunsResponse shape.
export type { BhaRunsResponse };

const bhaApi = pApi
  .enhanceEndpoints({ addTagTypes: ["BhaRuns", "BhaFullJob"] })
  .injectEndpoints({
    endpoints: (b) => ({
      // Re-declared list read, tagged so mutations below can invalidate it.
      getBhaRuns: b.query<BhaRunsListResponse, string>({
        query: (eventId) => `/events/${encodeURIComponent(eventId)}/bha-runs`,
        providesTags: (_r, _e, eventId) => [{ type: "BhaRuns", id: eventId }],
      }),

      // Full-job BHA roll-up: summary by section + by Run across the well.
      getBhaFullJob: b.query<BhaFullJobResponse, string>({
        query: (wellUwi) => `/wells/${encodeURIComponent(wellUwi)}/full-job/bha`,
        providesTags: (_r, _e, wellUwi) => [{ type: "BhaFullJob", id: wellUwi }],
      }),

      createBhaRun: b.mutation<
        BhaRunWithEntity,
        { eventId: string; body: BhaRunCreate }
      >({
        query: ({ eventId, body }) => ({
          url: `/events/${encodeURIComponent(eventId)}/bha-runs`,
          method: "POST",
          body,
        }),
        // Refetch the event's runs list and the well's full-job roll-up.
        invalidatesTags: (_r, _e, { eventId, body }) => [
          { type: "BhaRuns", id: eventId },
          { type: "BhaFullJob", id: body.well_uwi },
        ],
      }),

      updateBhaRun: b.mutation<
        BhaRunWithEntity,
        { eventId: string; entityId: string; wellUwi: string; body: BhaRunUpdate }
      >({
        query: ({ eventId, entityId, body }) => ({
          url: `/events/${encodeURIComponent(eventId)}/bha-runs/${encodeURIComponent(entityId)}`,
          method: "PUT",
          body,
        }),
        invalidatesTags: (_r, _e, { eventId, wellUwi }) => [
          { type: "BhaRuns", id: eventId },
          { type: "BhaFullJob", id: wellUwi },
        ],
      }),
    }),
  });

export const {
  useGetBhaRunsQuery,
  useGetBhaFullJobQuery,
  useCreateBhaRunMutation,
  useUpdateBhaRunMutation,
} = bhaApi;
